"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useId, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import {
	Box,
	Button,
	IconButton,
	InputAdornment,
    Stack,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from "@/components/controls/base-controls";
import {
	MdOutlineVisibility,
	MdOutlineVisibilityOff,
} from "@/components/controls/icons";

import {
	type PwResetConfirmData,
	type PwResetRequestData,
	pwResetConfirmSchema,
	pwResetRequestSchema,
} from "@/core/schemas/pw-reset.schema";
import {
	userConfirmResetPassword,
	userResetPassword,
} from "@/core/services/user.service";
import { useRouter } from "@/i18n/navigation";

// --- Step 2: Confirm with code + new password ---
const PwResetConfirmStep = ({
	email,
	onSuccess,
	onBackToSignIn,
}: {
	email: string;
	onSuccess: () => void;
	onBackToSignIn: () => void;
}) => {
	const t = useTranslations();

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	const codeId = useId();
	const passwordId = useId();
	const confirmPasswordId = useId();

	const [errorMessage, setErrorMessage] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);

	const confirmForm = useForm<PwResetConfirmData>({
		resolver: zodResolver(pwResetConfirmSchema),
		defaultValues: { code: "", password: "", password_confirmation: "" },
		mode: "onTouched",
	});

	const submitResetConfirm = useMutation({
		mutationFn: async (data: PwResetConfirmData) => {
			return await userConfirmResetPassword({
				email,
				code: data.code,
				newPassword: data.password,
			});
		},
		onSuccess: () => {
			setErrorMessage("");
			onSuccess();
		},
		onError: (error) => {
			console.error("Confirm reset password error:", error);
			setErrorMessage(t("pw_reset.error"));
		},
	});

	const onConfirmSubmit = (data: PwResetConfirmData) => {
		submitResetConfirm.mutate(data);
	};

	return (
		<>
			<Typography variant="h3" sx={{ marginBottom: "1.5em" }}>
				{t("pw_reset.confirmTitle")}
			</Typography>

			<Typography
				variant="body2"
				sx={{ maxWidth: "400px", textAlign: "center", mb: 2 }}
			>
				{t("pw_reset.confirmDescription")}
			</Typography>

			<Box
				component="form"
				noValidate
				onSubmit={confirmForm.handleSubmit(onConfirmSubmit)}
				sx={{
					display: "flex",
					flexDirection: "column",
					rowGap: 2,
					pt: "1em",
					width: "100%",
					maxWidth: "400px",
				}}
			>
				<Controller
					name="code"
					control={confirmForm.control}
					render={({ field }) => (
						<TextField
							{...field}
							fullWidth
							required
							id={codeId}
							label={t("confirmation.label")}
							error={!!confirmForm.formState.errors.code}
							helperText={
								confirmForm.formState.errors.code?.message &&
								t(confirmForm.formState.errors.code.message as string)
							}
							slotProps={{
								htmlInput: { maxLength: 8 },
								inputLabel: { shrink: isMobile ? true : undefined },
							}}
							variant="outlined"
							type="text"
						/>
					)}
				/>

				<Controller
					name="password"
					control={confirmForm.control}
					render={({ field }) => (
						<TextField
							{...field}
							fullWidth
							required
							id={passwordId}
							label={t("password.label")}
							error={!!confirmForm.formState.errors.password}
							helperText={
								confirmForm.formState.errors.password?.message
									? t(
											confirmForm.formState.errors.password
												.message as string,
										)
									: t("password.rules")
							}
							type={showPassword ? "text" : "password"}
							variant="outlined"
							autoComplete="new-password"
							slotProps={{
								htmlInput: { maxLength: 24 },
								inputLabel: { shrink: isMobile ? true : undefined },
								input: {
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												aria-label={
													showPassword
														? "Hide the password"
														: "Display the password"
												}
												onClick={() => setShowPassword(!showPassword)}
												onMouseDown={(e) => e.preventDefault()}
												edge="end"
											>
												{showPassword ? (
													<MdOutlineVisibilityOff />
												) : (
													<MdOutlineVisibility />
												)}
											</IconButton>
										</InputAdornment>
									),
								},
							}}
						/>
					)}
				/>

				<Controller
					name="password_confirmation"
					control={confirmForm.control}
					render={({ field }) => (
						<TextField
							{...field}
							fullWidth
							required
							id={confirmPasswordId}
							label={t("password_confirmation.label")}
							error={
								!!confirmForm.formState.errors.password_confirmation
							}
							helperText={
								confirmForm.formState.errors.password_confirmation
									?.message &&
								t(
									confirmForm.formState.errors.password_confirmation
										.message as string,
								)
							}
							type={showConfirmPassword ? "text" : "password"}
							variant="outlined"
							autoComplete="new-password"
							slotProps={{
								htmlInput: { maxLength: 24 },
								inputLabel: { shrink: isMobile ? true : undefined },
								input: {
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												aria-label={
													showConfirmPassword
														? "Hide the password"
														: "Display the password"
												}
												onClick={() =>
													setShowConfirmPassword(!showConfirmPassword)
												}
												onMouseDown={(e) => e.preventDefault()}
												edge="end"
											>
												{showConfirmPassword ? (
													<MdOutlineVisibilityOff />
												) : (
													<MdOutlineVisibility />
												)}
											</IconButton>
										</InputAdornment>
									),
								},
							}}
						/>
					)}
				/>

				{errorMessage && (
					<Typography variant="error">{errorMessage}</Typography>
				)}

				<Button
					variant="contained"
					color="secondary"
					sx={{
						margin: "2em 0 .5em",
						textTransform: "none",
						width: "100%",
					}}
					type="submit"
					disabled={submitResetConfirm.isPending}
				>
					{t("pw_reset.confirmSubmit")}
				</Button>

				<Stack
					sx={{
						rowGap: 2,
						width: "100%",
						maxWidth: "400px",
					}}
				>
					<Box
						sx={{
							display: "flex",
							flexDirection: "row",
							alignItems: "center",
							justifyContent: "center",
							width: "100%",
						}}
					>
						<Box sx={{ fontSize: "24px" }}>&#x291B;</Box>
						<Box sx={{ fontSize: "14px", position: "relative", top: "2px" }}>
							&nbsp;&nbsp;{t("misc.or")}&nbsp;&nbsp;
						</Box>
						<Box sx={{ fontSize: "24px" }}>&#x291C;</Box>
					</Box>

					<Button
						variant="square"
						type="button"
						onClick={onBackToSignIn}
						sx={{ textTransform: "none" }}
					>
						{t("pw_reset.backToSignIn")}
					</Button>
				</Stack>
			</Box>
		</>
	);
};

const PwResetPage = () => {
	const t = useTranslations();

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	const emailId = useId();

	const [step, setStep] = useState<"request" | "confirm" | "success">(
		"request",
	);
	const [email, setEmail] = useState("");
	const [errorMessage, setErrorMessage] = useState("");

	const router = useRouter();

	// --- Request form (step 1) ---
	const requestForm = useForm<PwResetRequestData>({
		resolver: zodResolver(pwResetRequestSchema),
		defaultValues: { email: "" },
		mode: "onTouched",
	});

	const submitResetRequest = useMutation({
		mutationFn: async (data: PwResetRequestData) => {
			return await userResetPassword({ email: data.email });
		},
		onSuccess: () => {
			setErrorMessage("");
			setStep("confirm");
		},
		onError: (error) => {
			console.error("Reset password request error:", error);
			if (error.name === "UserNotFoundException") {
				setErrorMessage(t("pw_reset.userNotFound"));
			} else {
				setErrorMessage(t("pw_reset.error"));
			}
		},
	});

	const onRequestSubmit = (data: PwResetRequestData) => {
		setEmail(data.email);
		submitResetRequest.mutate(data);
	};

	const handleBackToSignIn = () => {
		router.push("/sig");
	};

	// --- Step 1: Request reset code ---
	if (step === "request") {
		return (
			<>
				<Typography variant="h3" sx={{ marginBottom: "1.5em" }}>
					{t("pw_reset.title")}
				</Typography>

				<Typography
					variant="body2"
					sx={{ maxWidth: "400px", textAlign: "center", mb: 2 }}
				>
					{t("pw_reset.description")}
				</Typography>

				<Box
					component="form"
					noValidate
					onSubmit={requestForm.handleSubmit(onRequestSubmit)}
					sx={{
						display: "flex",
						flexDirection: "column",
						rowGap: 2,
						pt: "1em",
						width: "100%",
						maxWidth: "400px",
					}}
				>
					<Controller
						name="email"
						control={requestForm.control}
						render={({ field }) => (
							<TextField
								{...field}
								fullWidth
								required
								id={emailId}
								label={t("email.label")}
								placeholder={t("email.label")}
								autoComplete="username"
								error={!!requestForm.formState.errors.email}
								helperText={
									requestForm.formState.errors.email?.message &&
									t(requestForm.formState.errors.email.message as string)
								}
								variant="outlined"
								inputMode="email"
								type="email"
								slotProps={{
									inputLabel: { shrink: isMobile ? true : undefined },
								}}
							/>
						)}
					/>

					{errorMessage && (
						<Typography variant="error">{errorMessage}</Typography>
					)}

					<Button
						variant="contained"
						color="secondary"
						sx={{
							margin: "2em 0 .5em",
							textTransform: "none",
							width: "100%",
						}}
						type="submit"
						disabled={submitResetRequest.isPending}
					>
						{t("pw_reset.submit")}
					</Button>

                    <Stack
                        sx={{
                            rowGap: 2,
                            width: "100%",
                            maxWidth: "400px",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "100%",
                            }}
                        >
                            <Box sx={{ fontSize: "24px" }}>&#x291B;</Box>
                            <Box sx={{ fontSize: "14px", position: "relative", top: "2px" }}>
                                &nbsp;&nbsp;{t("misc.or")}&nbsp;&nbsp;
                            </Box>
                            <Box sx={{ fontSize: "24px" }}>&#x291C;</Box>
                        </Box>
        
                        <Button
                            variant="square"
                            type="button"
                            onClick={handleBackToSignIn}
                            sx={{ textTransform: "none" }}
                        >
                            {t("pw_reset.backToSignIn")}
                        </Button>
                    </Stack>
				</Box>
			</>
		);
	}

	// --- Step 2: Confirm with code + new password ---
	if (step === "confirm") {
		return (
			<PwResetConfirmStep
				email={email}
				onSuccess={() => setStep("success")}
				onBackToSignIn={handleBackToSignIn}
			/>
		);
	}

	// --- Step 3: Success ---
	return (
		<>
			<Typography variant="h3" sx={{ marginBottom: "1.5em" }}>
				{t("pw_reset.confirmTitle")}
			</Typography>

			<Typography
				variant="body2"
				sx={{ maxWidth: "400px", textAlign: "center", mb: 2 }}
			>
				{t("pw_reset.success")}
			</Typography>

			<Button
				variant="contained"
				color="secondary"
				sx={{
					margin: "2em 0 .5em",
					textTransform: "none",
					width: "100%",
					maxWidth: "400px",
				}}
				onClick={handleBackToSignIn}
			>
				{t("pw_reset.backToSignIn")}
			</Button>
		</>
	);
};

export default PwResetPage;
